import React, { useState, useMemo } from 'react';
import { MdPeople, MdPhoneIphone, MdWarning } from 'react-icons/md';
import Modal from '../../ui/Modal';
import { Button, Notice } from '../../ui/PageShell';
import { FormGrid, Field, Input, Textarea, Select, RadioGroup, Checkbox } from '../../ui/Form';
import MultiPicker from '../../ui/MultiPicker';
import { sectionLabel } from '../../ui/labels';
import './notifications.css';
import { useAuth } from '../auth';

/**
 * The compose dialog: WHO gets it, then WHAT it says, then a review
 * step that states the audience size in plain words before anything is
 * sent.
 *
 * Two deliberate choices, both fixing real faults in v1's form:
 *
 * 1. ONE audience object, not four target types dispatched to four
 *    endpoints. v1's "specific students" mode sent one HTTP request per
 *    student, so 40 pupils meant 40 requests and a partial failure left
 *    an unknowable half-sent state. Here the audience is data the
 *    server expands once, in one batch.
 * 2. A review step. The largest mistake this screen can make is
 *    broadcasting the whole school by accident, and the only cheap
 *    guard is showing the number out loud before the send.
 */

export const TYPES = [
  { value: 'announcement', label: 'إعلان عام' },
  { value: 'exam_created', label: 'امتحان جديد' },
  { value: 'exam_result', label: 'نتيجة امتحان' },
  { value: 'attendance_absent', label: 'غياب' },
  { value: 'attendance_late', label: 'تأخير' },
  { value: 'assignment_created', label: 'واجب جديد' },
  { value: 'invoice_due', label: 'قسط مستحق' },
  { value: 'payment_received', label: 'دفعة مستلمة' },
  { value: 'important_update', label: 'تحديث مهم' },
];

export const typeLabel = (v) => TYPES.find((t) => t.value === v)?.label || v || '—';

/** Which badge tone a type gets in the table — warnings warm, money
 *  neutral-info, plain announcements neutral. */
export const typeTone = (v) => {
  if (v === 'attendance_absent' || v === 'important_update') return 'danger';
  if (v === 'attendance_late' || v === 'invoice_due') return 'warning';
  if (v === 'exam_result' || v === 'payment_received') return 'success';
  if (v === 'exam_created' || v === 'assignment_created') return 'info';
  return 'neutral';
};

const AUDIENCE_KINDS = [
  { value: 'all_students', label: 'كل الطلاب' },
  { value: 'grades', label: 'صفوف' },
  { value: 'sections', label: 'شعب' },
  { value: 'students', label: 'طلاب محددون' },
  { value: 'teachers', label: 'المعلمون' },
  { value: 'admins', label: 'المدراء' },
];
// Kinds that reach staff, never pupils — so no «أولياء الأمور» option.
const STAFF_KINDS = ['teachers', 'admins'];
// A teacher writes to their own classes only; the API enforces the
// same list (notifications.js TEACHER_KINDS).
const TEACHER_KINDS = ['sections', 'students'];

const EMPTY = {
  kind: 'sections',
  grade_ids: [],
  section_ids: [],
  student_ids: [],
  teacher_ids: [],
  all_teachers: false,
  notify_parents: true,
  type: 'announcement',
  title: '',
  message: '',
};

export function emptyCompose() { return { ...EMPTY }; }

/**
 * Describe the chosen audience the way a person would say it out loud.
 * The same function renders the review line here and the "sent to"
 * column in the history table, so the two can never disagree.
 */
export function describeAudience(a, ref) {
  if (!a) return '—';
  // `ref` is null, not undefined, until useData's first load resolves —
  // a default parameter would not catch that, so destructure defensively.
  const { grades = [], sections = [] } = ref || {};
  const name = (list, id, fallback) => {
    const found = list.find((x) => String(x.id) === String(id));
    return found ? (found.section_label ? sectionLabel(found) : found.title) : fallback;
  };

  let base;
  switch (a.kind) {
    case 'all_students': base = 'كل الطلاب'; break;
    case 'admins': base = 'المدراء'; break;
    case 'teachers':
      base = a.all_teachers ? 'كل المعلمين' : `${(a.teacher_ids || []).length} معلم`;
      break;
    case 'grades': {
      const ids = a.grade_ids || [];
      base = ids.length === 1
        ? `الصف ${name(grades, ids[0], ids[0])}`
        : `${ids.length} صفوف`;
      break;
    }
    case 'sections': {
      const ids = a.section_ids || [];
      base = ids.length === 1
        ? name(sections, ids[0], `شعبة #${ids[0]}`)
        : `${ids.length} شعب`;
      break;
    }
    case 'students': {
      const n = (a.student_ids || []).length;
      base = n === 1 ? 'طالب واحد' : `${n} طلاب`;
      break;
    }
    default: base = '—';
  }
  // Parents only ever apply to student-side audiences.
  if (!STAFF_KINDS.includes(a.kind) && a.notify_parents) base += ' + أولياء الأمور';
  return base;
}

export default function NotificationCompose({
  form,
  setForm,
  refData,
  isTeacher = false,
  onClose,
  onSend,
  sending,
  dirty,
  estimate,
}) {
  const { isAdmin } = useAuth();
  // Fee notices are the manager's.
  const types = isAdmin ? TYPES : TYPES.filter((t) => !['invoice_due', 'payment_received'].includes(t.value));
  const kinds = isTeacher ? AUDIENCE_KINDS.filter((k) => TEACHER_KINDS.includes(k.value))
    : isAdmin ? AUDIENCE_KINDS : AUDIENCE_KINDS.filter((k) => k.value !== 'admins');
  const [step, setStep] = useState('compose'); // 'compose' | 'review'
  const [errors, setErrors] = useState({});

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setEvent = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const gradeOptions = useMemo(
    () => (refData?.grades || []).map((g) => ({ value: g.id, label: `الصف ${g.title}` })),
    [refData]
  );
  const sectionOpts = useMemo(
    () => (refData?.sections || [])
      .filter((s) => !form.grade_ids?.length || form.grade_ids.map(String).includes(String(s.grade_id)))
      .map((s) => ({ value: s.id, label: sectionLabel(s), hint: s.student_count != null ? `${s.student_count} طالب` : null })),
    [refData, form.grade_ids]
  );
  const studentOpts = useMemo(
    () => (refData?.students || []).filter((s) => s.status === 'active')
      .map((s) => ({ value: s.id, label: s.name, hint: s.section_label ? sectionLabel(s) : null })),
    [refData]
  );
  const teacherOpts = useMemo(
    () => (refData?.teachers || []).filter((t) => t.status !== 'inactive').map((t) => ({ value: t.id, label: t.name })),
    [refData]
  );

  const validate = () => {
    const e = {};
    if (form.kind === 'grades' && !form.grade_ids.length) e.audience = 'اختر صفاً واحداً على الأقل';
    if (form.kind === 'sections' && !form.section_ids.length) e.audience = 'اختر شعبة واحدة على الأقل';
    if (form.kind === 'students' && !form.student_ids.length) e.audience = 'اختر طالباً واحداً على الأقل';
    if (form.kind === 'teachers' && !form.all_teachers && !form.teacher_ids.length) e.audience = 'اختر معلماً واحداً على الأقل';
    if (!form.title.trim()) e.title = 'العنوان مطلوب';
    if (!form.message.trim()) e.message = 'الرسالة مطلوبة';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goReview = () => { if (validate()) setStep('review'); };

  const isBroadcast = form.kind === 'all_students' || (form.kind === 'teachers' && form.all_teachers);

  return (
    <Modal
      open
      size="lg"
      title={step === 'compose' ? 'إشعار جديد' : 'مراجعة قبل الإرسال'}
      onClose={onClose}
      confirmClose={dirty?.isDirty}
      footer={(requestClose) => (
        step === 'compose' ? (
          <>
            <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
            <Button onClick={goReview} data-guide="notif-next">التالي: مراجعة</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setStep('compose')}>رجوع</Button>
            <Button variant="success" onClick={onSend} loading={sending} data-guide="notif-send">
              {sending ? 'جارٍ الإرسال...' : 'إرسال الآن'}
            </Button>
          </>
        )
      )}
    >
      {step === 'compose' ? (
        <FormGrid columns={1}>
          <Field label="إرسال إلى" required error={errors.audience} guide="notif-audience">
            <RadioGroup
              value={form.kind}
              onChange={(v) => setForm((f) => ({ ...f, kind: v }))}
              options={kinds}
            />
          </Field>

          {form.kind === 'grades' && (
            <Field label="الصفوف" hint="يمكن اختيار أكثر من صف" guide="notif-pick">
              <MultiPicker options={gradeOptions} value={form.grade_ids} onChange={set('grade_ids')} placeholder="ابحث عن صف..." />
            </Field>
          )}

          {form.kind === 'sections' && (
            <>
              <Field label="تصفية بالصف (اختياري)" hint="يختصر قائمة الشعب فقط، ولا يُرسل للصف كله">
                <MultiPicker options={gradeOptions} value={form.grade_ids} onChange={set('grade_ids')} placeholder="ابحث عن صف..." height={130} />
              </Field>
              <Field label="الشعب" required guide="notif-pick">
                <MultiPicker options={sectionOpts} value={form.section_ids} onChange={set('section_ids')} placeholder="ابحث عن شعبة..." />
              </Field>
            </>
          )}

          {form.kind === 'students' && (
            <Field label="الطلاب" required hint="ابحث بالاسم، ثم أضف من تريد" guide="notif-pick">
              <MultiPicker options={studentOpts} value={form.student_ids} onChange={set('student_ids')} placeholder="ابحث عن طالب..." height={280} />
            </Field>
          )}

          {form.kind === 'teachers' && (
            <>
              <Field label="النطاق">
                <Checkbox
                  label="كل المعلمين"
                  checked={form.all_teachers}
                  onChange={(e) => setForm((f) => ({ ...f, all_teachers: e.target.checked, teacher_ids: [] }))}
                />
              </Field>
              {!form.all_teachers && (
                <Field label="المعلمون" required guide="notif-pick">
                  <MultiPicker options={teacherOpts} value={form.teacher_ids} onChange={set('teacher_ids')} placeholder="ابحث عن معلم..." />
                </Field>
              )}
            </>
          )}

          {!STAFF_KINDS.includes(form.kind) && (
            <Field label="أولياء الأمور">
              <Checkbox
                label="إرسال لأولياء الأمور أيضاً"
                checked={form.notify_parents}
                onChange={(e) => setForm((f) => ({ ...f, notify_parents: e.target.checked }))}
              />
            </Field>
          )}

          <Field label="النوع">
            <Select value={form.type} onChange={setEvent('type')} options={types} />
          </Field>
          <Field label="العنوان" required error={errors.title} guide="notif-title">
            <Input value={form.title} onChange={setEvent('title')} maxLength={120} placeholder="عنوان قصير وواضح" error={errors.title} />
          </Field>
          <Field label="الرسالة" required error={errors.message} hint={`${form.message.length} حرف`} guide="notif-message">
            <Textarea value={form.message} onChange={setEvent('message')} rows={5} error={errors.message} />
          </Field>
        </FormGrid>
      ) : (
        <div className="notif-review">
          {isBroadcast && (
            <Notice tone="warning" title="إرسال للجميع">
              <MdWarning size={15} style={{ verticalAlign: '-2px' }} />{' '}
              هذا الإشعار سيصل كل من في القائمة. تأكد من النص قبل الإرسال.
            </Notice>
          )}

          <div className="notif-review-stats">
            <div className="notif-stat">
              <MdPeople size={20} />
              <strong>{estimate?.people ?? '—'}</strong>
              <span>مستلم</span>
            </div>
            <div className="notif-stat">
              <MdPhoneIphone size={20} />
              <strong>{estimate?.devices ?? '—'}</strong>
              <span>جهاز مفعّل للإشعارات</span>
            </div>
          </div>

          <dl className="notif-review-list">
            <div><dt>الجهة</dt><dd>{describeAudience(form, refData)}</dd></div>
            <div><dt>النوع</dt><dd><span className={`ui-badge ui-badge-${typeTone(form.type)}`}>{typeLabel(form.type)}</span></dd></div>
            <div><dt>العنوان</dt><dd>{form.title}</dd></div>
            <div><dt>الرسالة</dt><dd className="notif-review-message">{form.message}</dd></div>
          </dl>

          <p className="notif-review-note">
            من لا يملك جهازاً مفعّلاً سيجد الإشعار داخل التطبيق عند دخوله.
          </p>
        </div>
      )}
    </Modal>
  );
}
